import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { ArticleService } from '../../../service/article.service';
import { Article } from '../../../models/Article';
import { ModelEditArticle } from '../model-edit-article/model-edit-article';
import { CatalogoService } from '../../../service/catalogo.service';
import { ExportArticlesExcelComponent } from '../export-articles-excel/export-articles-excel';
import { GeneralService } from '../../../service/general.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-search-article',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModelEditArticle,
    ExportArticlesExcelComponent, AgGridAngular],
  templateUrl: './search-article.html',
  styleUrl: './search-article.scss'
})
export class SearchArticle {


  private fb = inject(FormBuilder);
  private articleService = inject(ArticleService);
  private catalogService = inject(CatalogoService);
  private generalService = inject(GeneralService);

  searchForm = this.fb.group({
    term: [''],
    estado: ['TODOS'],
    variedad: [0]
  });

  loading = signal(false);
  error = signal<string | null>(null);
  hasSearched = signal(false);
  articles = signal<Article[]>([]);
  secciones = signal<any[]>([]);
  divisiones = signal<any[]>([]);
  clases = signal<any[]>([]);
  productos = signal<any[]>([]);
  variedades = signal<any[]>([]);
  unidadesMedida = signal<any[]>([]);


  selectedArticle: Article | null = null;
  ngOnInit(): void {
    this.catalogService.getSecciones().subscribe(r => this.secciones.set(r));
    this.catalogService.getDivisiones(0).subscribe(r => this.divisiones.set(r));
    this.catalogService.getClases(0).subscribe(r => this.clases.set(r));
    this.catalogService.getProductos(0).subscribe(r => this.productos.set(r));
    this.catalogService.getVariedades(0).subscribe(r => this.variedades.set(r));
    this.generalService
      .get('GET_UNIDADES_MEDIDA')
      .subscribe(r => this.unidadesMedida.set(r));
  }
  getUnidadMedidaName(id: number | null | undefined): string {
    if (!id) return '';
    return this.unidadesMedida().find(u => u.pk === id)?.descripcion ?? '';
  }


  onSearch() {
    const term = this.searchForm.value.term?.trim().toLowerCase() || '';
    const estado = this.searchForm.value.estado || 'TODOS';
    const variedad = Number(this.searchForm.value.variedad ?? 0);

    this.loading.set(true);
    this.error.set(null);

    this.articleService.getAll().subscribe({
      next: data => {
        this.loading.set(false);
        this.hasSearched.set(true);

        let result = data;

        if (term) {
          result = result.filter(a =>
            a.nombre?.toLowerCase().includes(term) ||
            a.codigoIne?.toLowerCase().includes(term)
          );
        }

        if (estado !== 'TODOS') {
          result = result.filter(a => a.estado === estado);
        }

        if (variedad !== 0) {
          result = result.filter(a => Number(a.fkVariedad) === variedad);
        }

        this.articles.set(result);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al buscar artículos.');
      }
    });
  }


  onListAll() {
    this.searchForm.get('term')?.setValue('');

    this.loading.set(true);
    this.error.set(null);
    this.hasSearched.set(true);

    const estado = this.searchForm.value.estado || 'TODOS';
    const variedad = Number(this.searchForm.value.variedad ?? 0);

    this.articleService.getAll().subscribe({
      next: data => {
        let result = data;

        if (estado !== 'TODOS') {
          result = result.filter(a => a.estado === estado);
        }

        if (variedad !== 0) {
          result = result.filter(a => Number(a.fkVariedad) === variedad);
        }

        this.articles.set(result);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Error al consultar artículos.');
      }
    });
  }
  onEdit(article: Article) {
    this.selectedArticle = article;
    const modal = new (window as any).bootstrap.Modal(
      document.getElementById("modalEditArticle")
    );
    modal.show();
  }

  onArticleDeleted(codigo: number) {
    this.articles.update(list => list.filter(a => a.codigo !== codigo));
    this.selectedArticle = null;
  }

  onArticleUpdated(updated: Article) {
    this.articles.update(list =>
      list.map(a => a.codigo === updated.codigo ? updated : a)
    );
  }
  getSeccionName(id: number | null): string {
    if (!id) return '';
    return this.secciones().find(s => s.codigo === id)?.nombre ?? '';
  }

  getDivisionName(id: number | null): string {
    if (!id) return '';
    return this.divisiones().find(d => d.codigo === id)?.nombre ?? '';
  }

  getClaseName(id: number | null): string {
    if (!id) return '';
    return this.clases().find(c => c.codigo === id)?.nombre ?? '';
  }

  getProductoName(id: number | null): string {
    if (!id) return '';
    return this.productos().find(p => p.codigo === id)?.nombre ?? '';
  }

  getVariedadName(id: number | null): string {
    if (!id) return '';
    return this.variedades().find(v => v.codigo === id)?.nombre ?? '';
  }
  onClear() {
    this.searchForm.patchValue({
      term: '',
      estado: 'TODOS',
      variedad: 0
    });

    this.error.set(null);
    this.hasSearched.set(false);
    this.articles.set([]);
  }
  columnDefs: ColDef[] = [
    {
      headerName: 'Código INE',
      field: 'codigoIne'
    },
    {
      headerName: 'Nombre',
      field: 'nombre',
      flex: 1
    },
    {
      headerName: 'Estado',
      field: 'estado',
      cellRenderer: (params: any) => {
        const estado = (params.value ?? '').trim().toUpperCase();

        if (estado === 'A') {
          return `<span style="color:#198754; font-weight:700;">Activo</span>`;
        }

        if (estado === 'P') {
          return `<span style="color:#ffc107; font-weight:700;">Pendiente</span>`;
        }

        if (estado === 'I') {
          return `<span style="color:#dc3545; font-weight:700;">Inactivo</span>`;
        }

        return `<span style="color:#6c757d; font-weight:700;">Desconocido</span>`;
      }
    },
    {
      headerName: 'Acciones',
      colId: 'acciones',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: () => `
      <button
        type="button"
        class="btn btn-sm btn-outline-info"
        title="Editar información del artículo">
        <i class="bi bi-pencil-square"></i>
      </button>
    `
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    filter: false,
    floatingFilter: false
  };

  onCellClicked(event: any) {
    if (event.colDef.colId === 'acciones') {
      this.onEdit(event.data);
    }
  }
  localeText = {
    contains: 'Contiene',
    notContains: 'No contiene',
    equals: 'Igual',
    notEqual: 'Diferente',
    startsWith: 'Empieza con',
    endsWith: 'Termina con',

    filterOoo: 'Filtrar...',
    applyFilter: 'Aplicar',
    resetFilter: 'Limpiar',
    clearFilter: 'Limpiar',
    cancelFilter: 'Cancelar',

    noRowsToShow: 'No hay registros para mostrar',

    page: 'Página',
    more: 'Más',
    to: 'a',
    of: 'de',
    next: 'Siguiente',
    last: 'Último',
    first: 'Primero',
    previous: 'Anterior',

    loadingOoo: 'Cargando...',
    searchOoo: 'Buscar...'
  };

}